import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPaperPlane, FaUserCircle, FaCheck, FaTimes, FaSearch, FaComments, FaSpinner, FaExchangeAlt } from 'react-icons/fa';
import { searchUsersByName, sendFriendRequest, getFriendRequests, getOutgoingRequests, acceptFriendRequest, denyFriendRequest, getFriends, revokeOutgoingRequest } from './SocialFunctions';
import { useFireAuth } from '../FireAuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../App';
import DelayRender from './DelayRender';
import useInterval from './useInterval';
import ChatRoom from './ChatRoom';
import MembershipTrophy from './MembershipTrophy';
import FriendCard from './FriendCard';
import './Social.css';
import Modal from 'react-modal';

const Social = () => {
  const { user, loading } = useFireAuth();
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [message, setMessage] = useState('');
  const [showChatRoom, setShowChatRoom] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openTradesCount, setOpenTradesCount] = useState(0);

  const toggleChatRoom = () => {
    setShowChatRoom(prev => !prev);
  };

  const loadFriendRequests = useCallback(async () => {
    if (user && user.uid) {
      const requests = await getFriendRequests(user.uid);
      setFriendRequests(requests);
    }
  }, [user]);

  const loadOutgoingRequests = useCallback(async () => {
    if (user && user.uid) {
      const requests = await getOutgoingRequests(user.uid);
      setOutgoingRequests(requests);
    }
  }, [user]);

  const loadFriends = useCallback(async () => {
    if (user && user.uid) {
      setLoadingFriends(true);
      const friendsList = await getFriends(user.uid);
      const friendData = await Promise.all(
        friendsList.map(async (friend) => {
          const friendDocRef = doc(db, 'users', friend.friendId);
          const friendDocSnap = await getDoc(friendDocRef);
          if (friendDocSnap.exists()) {
            return { ...friendDocSnap.data(), id: friend.friendId };
          } else {
            return null;
          }
        })
      );
      const sortedFriends = friendData.filter(friend => friend !== null).sort((a, b) => a.name.localeCompare(b.name));
      setFriends(sortedFriends);
      setLoadingFriends(false);
    }
  }, [user]);

  const loadFriendsInBackground = useCallback(async () => {
    if (user && user.uid) {
      const friendsList = await getFriends(user.uid);
      const friendData = await Promise.all(
        friendsList.map(async (friend) => {
          const friendDocRef = doc(db, 'users', friend.friendId);
          const friendDocSnap = await getDoc(friendDocRef);
          if (friendDocSnap.exists()) {
            return { ...friendDocSnap.data(), id: friend.friendId };
          } else {
            return null;
          }
        })
      );
      const sortedFriends = friendData.filter(friend => friend !== null).sort((a, b) => a.name.localeCompare(b.name));
      setFriends(sortedFriends);
    }
  }, [user]);

  const loadOpenTradesCount = useCallback(async () => {
    if (user && user.uid) {
      const tradesRef = collection(db, 'trades');
      const q1 = query(tradesRef, where('from', '==', user.uid), where('status', 'not-in', ['completed', 'cancelled']));
      const q2 = query(tradesRef, where('to', '==', user.uid), where('status', 'not-in', ['completed', 'cancelled']));
      const querySnapshot1 = await getDocs(q1);
      const querySnapshot2 = await getDocs(q2);
      const openTrades = querySnapshot1.size + querySnapshot2.size;
      setOpenTradesCount(openTrades);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadFriendRequests();
      loadOutgoingRequests();
      loadFriends();
      loadOpenTradesCount();
    }
  }, [user, loadFriendRequests, loadOutgoingRequests, loadFriends, loadOpenTradesCount]);

  useInterval(() => {
    if (user) {
      loadFriendRequests();
      loadOutgoingRequests();
      loadFriendsInBackground();
      loadOpenTradesCount();
    }
  }, 30000);

  const handleSearch = async (e) => {
    e.preventDefault();
    setMessage('');
    if (searchName.trim().length < 2) {
      setSearchResults([]);
      setMessage('Please enter at least 2 characters to search.');
      return;
    }
    try {
      const results = await searchUsersByName(searchName);
      setSearchResults(results);
      if (results.length === 0) {
        setMessage('No users found with that name.');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setMessage('Failed to search users. Please try again later.');
    }
  };

  const handleSendRequest = async (friendId, friendName) => {
    if (!user || !user.uid || !user.name) {
      console.error('User ID or name is not available');
      return;
    }

    if (user.uid === friendId) {
      setMessage('You cannot send a friend request to yourself.');
      return;
    }

    const isDuplicateRequest = outgoingRequests.some(request => request.to === friendId);
    if (isDuplicateRequest) {
      setMessage('You have already sent a friend request to this user.');
      return;
    }

    const isAlreadyFriend = friends.some(friend => friend.id === friendId);
    if (isAlreadyFriend) {
      setMessage('This user is already your friend.');
      return;
    }

    try {
      await sendFriendRequest(user.uid, user.name, friendId, friendName);
      setMessage('Friend request sent!');
      setSearchResults(searchResults.filter(result => result.id !== friendId));
      await loadOutgoingRequests();
    } catch (error) {
      console.error('Error sending friend request:', error);
      setMessage('Failed to send friend request. Please try again later.');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    if (user && user.uid && user.name) {
      try {
        await acceptFriendRequest(user.uid, user.name, requestId);
        loadFriendRequests();
        loadFriends();
      } catch (error) {
        console.error('Error accepting friend request:', error);
        setMessage('Failed to accept friend request. Please try again later.');
      }
    } else {
      console.error('User ID is not available');
    }
  };

  const handleDenyRequest = async (requestId) => {
    if (user && user.uid) {
      try {
        await denyFriendRequest(user.uid, requestId);
        loadFriendRequests();
      } catch (error) {
        console.error('Error denying friend request:', error);
        setMessage('Failed to deny friend request. Please try again later.');
      }
    } else {
      console.error('User ID is not available');
    }
  };

  const handleRevokeOutgoingRequest = async (requestId) => {
    if (user && user.uid) {
      try {
        await revokeOutgoingRequest(user.uid, requestId);
        loadOutgoingRequests();
      } catch (error) {
        console.error('Error revoking outgoing request:', error);
        setMessage('Failed to revoke outgoing request. Please try again later.');
      }
    } else {
      console.error('User ID is not available');
    }
  };

  const openFriendCardModal = (friend) => {
    setSelectedFriend(friend);
    setIsModalOpen(true);
  };

  const closeFriendCardModal = () => {
    setSelectedFriend(null);
    setIsModalOpen(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <div>No user logged in</div>;
  }

  return (
    <div className="page">
      <div className="page-container">
        {message && <div className="message">{message}</div>}
        <div className="friend-request-form">
          <input
            type="text"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Search by name"
            className="search-input"
          />
          <FaSearch onClick={handleSearch} className="search-icon" />
        </div>
        <div className="search-results">
          {searchResults.map((result) => (
            <div key={result.id} className="user-card">
              <div className="user-info">
                {result.profilePicture ? (
                  <img src={result.profilePicture} alt={`${result.name}'s profile`} className="profile-pic" />
                ) : (
                  <FaUserCircle className="profile-pic-placeholder" />
                )}
                <div className="user-details">
                  <span className="user-name">
                    {result.name}
                    <span className="membership-number">#{result.membershipNumber}</span>
                  </span>
                </div>
              </div>
              <div className="user-actions">
                <FaPaperPlane onClick={() => handleSendRequest(result.id, result.name)} className="send-request-icon" />
              </div>
            </div>
          ))}
        </div>
        <div className="friend-requests">
          <h3>📥 Inbox</h3>
          {friendRequests.map((request) => (
            <div key={request.id} className="user-card">
              <div className="user-info">
                {request.fromProfilePicture ? (
                  <img src={request.fromProfilePicture} alt={`${request.fromName}'s profile`} className="profile-pic" />
                ) : (
                  <FaUserCircle className="profile-pic-placeholder" />
                )}
                <div className="user-details">
                  <span className="user-name">
                    {request.fromName}
                    <span className="membership-number">#{request.fromMembershipNumber}</span>
                  </span>
                </div>
              </div>
              <div className="user-actions">
                <FaCheck onClick={() => handleAcceptRequest(request.id)} className="accept-request-icon" />
                <FaTimes onClick={() => handleDenyRequest(request.id)} className="deny-request-icon" />
              </div>
            </div>
          ))}
        </div>
        <div className="outgoing-requests-section">
          <h3>⏳ Outbox</h3>
          {outgoingRequests.map((request) => (
            <div key={request.id} className="user-card">
              <div className="user-info">
                {request.profilePicture ? (
                  <img src={request.profilePicture} alt={`${request.toName}'s profile`} className="profile-pic" />
                ) : (
                  <FaUserCircle className="profile-pic-placeholder" />
                )}
                <div className="user-details">
                  <span className="user-name">
                    {request.toName}
                    <span className="membership-number">#{request.toMembershipNumber}</span>
                  </span>
                </div>
              </div>
              <div className="user-actions">
                <FaTimes onClick={() => handleRevokeOutgoingRequest(request.id)} className="deny-request-icon" />
              </div>
            </div>
          ))}
        </div>
        <div className="friends-list">
          <h3>👫 Friends</h3>
          {loadingFriends ? (
            <div className="loading-icon"><FaSpinner className="fa-spin" /></div>
          ) : (
            friends.map((friend) => (
              <div
                key={friend.id}
                className="user-card"
                onClick={() => openFriendCardModal(friend)}
              >
                <div className="user-info">
                  {friend.profilePicture ? (
                    <img src={friend.profilePicture} alt={`${friend.name}'s profile`} className="profile-pic" />
                  ) : (
                    <FaUserCircle className="profile-pic-placeholder" />
                  )}
                  <div className="user-details">
                    <span className="user-name">
                      <MembershipTrophy status={friend.membershipStatus} />
                      {friend.name}
                      <span className="membership-number">#{friend.membershipNumber}</span>
                    </span>
                    <span className="user-status">{friend.status}</span>
                  </div>
                </div>
                <div className="user-actions">
                  <span className="user-money">💴 {friend.money.toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <FriendCard 
          isOpen={isModalOpen} 
          onRequestClose={closeFriendCardModal} 
          friendId={selectedFriend ? selectedFriend.id : null}
          updateUser={loadFriendsInBackground}
        />
      <div className="button-container">
        <button onClick={toggleChatRoom} className="open-chat-room-button">
          <FaComments /> {showChatRoom ? 'Close' : 'Open'} Chat Room
        </button>
        <button onClick={() => navigate('/portal/trades')} className="open-chat-room-button trading-button">
          <FaExchangeAlt /> Trading
          {openTradesCount > 0 && (
            <span className="notification-label">{openTradesCount}</span>
          )}
        </button>
      </div>
        <Modal
          isOpen={showChatRoom}
          onRequestClose={toggleChatRoom}
          contentLabel="Chat Room"
          className="chat-room-modal"
          overlayClassName="chat-room-overlay"
        >
          <button onClick={toggleChatRoom} className="close-chat-room-button">
            <FaTimes />
          </button>
          <ChatRoom />
        </Modal>
      </div>
    </div>
  );
};

export default DelayRender(Social, 300);
